import { Controller } from "@hotwired/stimulus"
import { interpolate } from "../lib/interpolate"

// Handles drag & drop file input with validation and progress display
// Dispatches events: file-drop:selected, file-drop:removed
export default class extends Controller {
  static targets = [
    "dropZone",
    "fileName",
    "fileSize",
    "fileInfo",
    "removeButton",
    "errorMessage",
    "serverErrorMessage",
    "progressBar",
    "progressContainer",
    "defaultState"
  ]

  static values = {
    fileInputId: { type: String, default: "upload_file_input" },
    maxSize: Number, // Set from server: Upload.max_file_size
    convertibleMaxSize: { type: Number, default: 0 }, // Higher limit for files converted before delivery
    convertibleExtensions: { type: Array, default: [] }, // Set from server: Upload.convertible_file_suffixes for this reader
    allowedExtensions: Array, // Set from server: Upload.allowed_file_suffixes
    allowedMimeTypes: Array, // Set from server: Upload.allowed_content_types
    messages: { type: Object, default: {} } // Localized errors from UploadsHelper#file_drop_messages
  }

  connect() {
    this.file = null
    this.errorTimeout = null
    this.disabled = false

    // Listen for Direct Upload events
    this.element.addEventListener("direct-upload:start", this.handleUploadStart.bind(this))
    this.element.addEventListener("direct-upload:progress", this.handleUploadProgress.bind(this))
    this.element.addEventListener("direct-upload:end", this.handleUploadEnd.bind(this))
  }

  disconnect() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }
  }

  // Public API for external control
  disable() {
    this.disabled = true
    this.dropZoneTargets.forEach(dropZone => {
      dropZone.classList.add("opacity-50", "pointer-events-none")
    })
  }

  enable() {
    this.disabled = false
    this.dropZoneTargets.forEach(dropZone => {
      dropZone.classList.remove("opacity-50", "pointer-events-none")
    })
  }

  hasFile() {
    return this.file !== null
  }

  // Reset to initial state (called after successful form submission)
  reset() {
    this.clearSelection()
    this.progressContainerTargets.forEach(el => el.classList.add("hidden"))
  }

  // Drag and drop handlers
  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.disabled) {
      this.setDragOverState(event.currentTarget)
    }
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setDefaultState()
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()

    this.acceptDroppedFiles(event.dataTransfer.files)
  }

  // The one way a drop reaches handleFile: from the drop zone and from the
  // rest of the upload form (upload-form#formDrop).
  acceptDroppedFiles(files) {
    if (this.disabled) {
      this.showError(this.message("busy"))
      return
    }

    if (files.length > 1) {
      this.showError(this.message("one_file"))
      return
    }

    if (files.length === 1) {
      this.handleFile(files[0])
    }
  }

  // Click handler to open file picker
  openFilePicker(event) {
    event.preventDefault()

    if (this.disabled) {
      this.showError(this.message("busy"))
      return
    }

    const fileInput = document.getElementById(this.fileInputIdValue)
    if (fileInput) {
      fileInput.click()
    }
  }

  // Called by upload-form controller when a file is selected via the input
  handleFileFromInput(file) {
    this.handleFile(file, true)
  }

  // File input change handler
  selectFile(event) {
    const files = event.target.files

    if (files.length > 0) {
      this.handleFile(files[0], true) // true = file came from input, not drag & drop
    }
  }

  // Remove file handler
  removeFile(event) {
    event.preventDefault()
    event.stopPropagation()
    this.clearSelection()
    this.dispatch("removed")
  }

  // A refused file ends the earlier selection, as removeFile does. The input
  // no longer holds the earlier file, so the form must stop counting it.
  rejectFile(message) {
    const hadFile = this.hasFile()
    this.clearSelection()
    this.showError(message)
    if (hadFile) {
      this.dispatch("removed")
    }
  }

  // Core file handling logic
  handleFile(file, fromInput = false) {
    this.clearError()

    // Validate file type
    if (!this.isValidFileType(file)) {
      this.rejectFile(this.message("wrong_type", { name: file.name }))
      return
    }

    // Validate file size
    const convertible = this.hasSuffix(file, this.convertibleExtensionsValue)
    const sizeLimit = convertible ? this.convertibleMaxSizeValue : this.maxSizeValue
    if (file.size > sizeLimit) {
      this.rejectFile(this.message(convertible ? "too_large_convertible" : "too_large", { name: file.name }))
      return
    }

    // File is valid
    this.file = file

    // If file came from drag & drop, we need to set it on the input element
    // so it gets submitted with the form
    const fileInput = document.getElementById(this.fileInputIdValue)
    if (!fromInput && fileInput) {
      this.setFileOnInput(file, fileInput)
    }

    this.setFileAcceptedState(file)
    this.dispatch("selected", { detail: { file } })
  }

  // Set file on the input element using DataTransfer API
  setFileOnInput(file, fileInput) {
    try {
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      fileInput.files = dataTransfer.files
    } catch (e) {
      // DataTransfer not supported in older browsers
      console.warn("Could not set file on input:", e)
    }
  }

  isValidFileType(file) {
    if (this.allowedMimeTypesValue.includes(file.type)) {
      return true
    }

    return this.hasSuffix(file, this.allowedExtensionsValue)
  }

  // A suffix can have two parts (fb2.zip), so match the end of the name, not
  // the last extension.
  hasSuffix(file, suffixes) {
    const name = file.name.toLowerCase()
    return suffixes.some(suffix => name.endsWith(`.${suffix}`))
  }

  formatFileSize(bytes) {
    if (bytes < 1024) {
      return bytes + " B"
    } else if (bytes < 1024 * 1024) {
      return (bytes / 1024).toFixed(1) + " KB"
    } else {
      return (bytes / (1024 * 1024)).toFixed(1) + " MB"
    }
  }

  // Visual state management
  setDefaultState() {
    this.dropZoneTargets.forEach(dropZone => {
      dropZone.classList.remove("border-warning", "bg-warning-bg", "border-solid", "border-error")
      dropZone.classList.add("border-dashed", "border-border", "bg-surface-secondary")
    })

    this.defaultStateTargets.forEach(el => el.classList.remove("hidden"))
    this.fileInfoTargets.forEach(el => el.classList.add("hidden"))

    this.clearError()
  }

  setDragOverState(dropZone) {
    if (dropZone) {
      dropZone.classList.remove("border-dashed", "border-border", "bg-surface-secondary", "border-error")
      dropZone.classList.add("border-warning", "bg-warning-bg")
    }
  }

  setFileAcceptedState(file) {
    this.dropZoneTargets.forEach(dropZone => {
      dropZone.classList.remove("border-dashed", "border-warning", "bg-warning-bg", "border-error")
      dropZone.classList.add("border-solid", "border-border", "bg-surface-secondary")
    })

    this.defaultStateTargets.forEach(el => el.classList.add("hidden"))
    this.fileInfoTargets.forEach(el => el.classList.remove("hidden"))
    this.fileNameTargets.forEach(el => el.textContent = file.name)
    this.fileSizeTargets.forEach(el => el.textContent = this.formatFileSize(file.size))
  }

  setErrorState() {
    this.dropZoneTargets.forEach(dropZone => {
      dropZone.classList.remove("border-warning", "bg-warning-bg")
      dropZone.classList.add("border-error", "border-dashed", "bg-surface-secondary")
    })
  }

  // Error handling
  message(key, values = {}) {
    return interpolate(this.messagesValue[key], values)
  }

  // Forget the selected file and empty the input, without telling the form.
  clearSelection() {
    this.file = null
    const fileInput = document.getElementById(this.fileInputIdValue)
    if (fileInput) {
      fileInput.value = ""
    }
    this.renderSelection()
  }

  renderSelection() {
    if (this.file) {
      this.setFileAcceptedState(this.file)
    } else {
      this.setDefaultState()
    }
  }

  showError(message) {
    this.setErrorState()

    this.errorMessageTargets.forEach(el => {
      el.textContent = message
      el.classList.remove("hidden")
    })

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
    }

    // When the error fades, show what is actually selected: a refused drop of
    // several files leaves the earlier file in place.
    this.errorTimeout = setTimeout(() => {
      this.clearError()
      this.renderSelection()
    }, 5000)
  }

  clearError() {
    this.errorMessageTargets.forEach(el => {
      el.classList.add("hidden")
      el.textContent = ""
    })
    this.serverErrorMessageTargets.forEach(el => {
      el.classList.add("hidden")
      el.textContent = ""
    })

    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout)
      this.errorTimeout = null
    }
  }

  // Direct Upload event handlers
  handleUploadStart(event) {
    this.progressContainerTargets.forEach(el => el.classList.remove("hidden"))
    this.fileInfoTargets.forEach(el => el.classList.add("hidden"))
    this.progressBarTargets.forEach(el => el.style.width = "0%")
  }

  handleUploadProgress(event) {
    const { progress } = event.detail
    this.progressBarTargets.forEach(el => el.style.width = `${progress}%`)
  }

  handleUploadEnd(event) {
    this.progressBarTargets.forEach(el => el.style.width = "100%")
  }
}
